import { io, Socket } from 'socket.io-client';
import type { VenueEventName } from '@/lib/shared-types';
import { refreshAccessTokens, type TokenSource } from './http';

/** The gateway's answer to a `join` (see `realtime.gateway.ts`). */
export interface JoinAck {
  joined: string[];
  denied: { room: string; reason: string }[];
}

/** What `connectRealtime` tells a token getter about the handshake it is for. */
export interface RealtimeTokenRequest {
  /** 1 for this socket's first handshake, incremented on every reconnect attempt. */
  attempt: number;
  /**
   * True when the gateway refused the credential handed over last time.
   *
   * A getter over a **refreshable** credential must not answer with the same
   * string here: re-presenting a token the server has already rejected is the
   * entire reconnect loop this parameter exists to break. Refresh, or answer
   * `null` and let the socket give up.
   */
  refused: boolean;
}

/**
 * Produces the access token for one handshake.
 *
 * Async on purpose — every native app keeps its tokens in the Keychain and a
 * read is a promise. Socket.IO's `auth` callback is happy to be called later,
 * and holds the CONNECT packet until it is.
 */
export type RealtimeTokenGetter = (
  request: RealtimeTokenRequest,
) => string | null | undefined | Promise<string | null | undefined>;

/** Why a socket stopped trying. */
export interface RealtimeAuthFailure {
  /** How many consecutive handshakes the gateway refused before it gave up. */
  attempts: number;
  /** The gateway's own words, when it sent any. */
  message?: string;
}

/**
 * How many consecutive refusals end the socket.
 *
 * Three, so the normal expiry path still heals itself without a human: the first
 * handshake is refused with the stale token, the second carries a refreshed one,
 * and only a credential that cannot be refreshed reaches the third.
 */
const DEFAULT_MAX_AUTH_FAILURES = 3;

/**
 * X-14 — the backoff this client drives *itself*, for the one case Socket.IO
 * refuses to: a server-initiated hang-up.
 *
 * Socket.IO reconnects from every disconnect reason except `io server
 * disconnect`, on the reading that a server which called `disconnect()` meant
 * it. The gateway calls `client.disconnect(true)` on any refused handshake, so
 * that reading is wrong here — and a backend whose pool is still cold refuses
 * one handshake on the way up.
 */
const RECONNECT_DELAY_MS = 1000;
const RECONNECT_DELAY_MAX_MS = 10_000;

/**
 * How long a connection must stand without being refused before its credential
 * counts as proven and the failure streak is forgiven.
 *
 * The gateway's verdict lands within a round trip of `connect` — it verifies and
 * hangs up immediately — so a connection still standing a second later was
 * accepted. Without this the streak only ever grew: an expiry healed by a
 * refresh still left a mark, and three healed expiries over a long shift ended
 * the socket as though the credential had been dead all along.
 */
const CREDENTIAL_SETTLE_MS = 1000;

/**
 * Timers, typed here rather than imported.
 *
 * This package compiles against `lib: ES2022` alone — no DOM, no `@types/node` —
 * because one build ships to React Native, Expo, browsers and Node, and the
 * `.d.ts` it publishes must not assert globals a given consumer does not have.
 * All four runtimes provide these two functions; none of them declares them at
 * this `lib` setting. The handle stays opaque on purpose: a `number` in a
 * browser, a `Timeout` object under Node, and this file only ever hands it back.
 */
interface Timers {
  setTimeout(handler: () => void, ms: number): unknown;
  clearTimeout(handle: unknown): void;
}
const timers = globalThis as unknown as Timers;
type TimerHandle = ReturnType<Timers['setTimeout']>;

export interface RealtimeOptions {
  baseURL: string;
  /**
   * A fixed token, captured once.
   *
   * Still honoured so the dashboards and the customer web app keep working
   * unchanged, but wrong for anything with an expiring credential: Socket.IO
   * reconnects on its own, and every one of those reconnects would re-present
   * this exact string. Prefer `getToken`.
   *
   * @deprecated Pass `getToken` instead — see `createRealtimeTokenGetter`.
   */
  token?: string;
  /**
   * Where a **current** token comes from, asked once per connection attempt.
   *
   * Wins over `token` when both are given.
   */
  getToken?: RealtimeTokenGetter;
  rooms?: string[];
  /**
   * Called whenever the gateway refuses a room. A denied room is the difference between "this
   * venue is quiet" and "this screen is not subscribed", and the only way a client can tell them
   * apart. Fires for every join, including the automatic rejoin.
   */
  onJoinDenied?: (denied: JoinAck['denied']) => void;
  /**
   * Consecutive refused handshakes before the socket stops retrying.
   * Defaults to 3; a value below 1 is treated as 1.
   */
  maxAuthFailures?: number;
  /**
   * The socket has given up: the gateway refused `maxAuthFailures` handshakes in
   * a row, reconnection is off, and this socket will never carry another event.
   *
   * **A caller that ignores this gets the bug back.** No UI is invented here —
   * that belongs to the app — but the app must both say so and drop whatever
   * cached reference it holds to this socket, or its next `getSocket()` hands
   * back a corpse.
   */
  onAuthFailure?: (failure: RealtimeAuthFailure) => void;
}

export interface RealtimeSocket extends Socket {
  /** Every room this socket has asked for since it was created. */
  readonly joinedRooms: ReadonlySet<string>;
  /** Join rooms and keep them across reconnects. */
  joinRooms(rooms: string[]): void;
  /**
   * Leave rooms, and stop replaying them on reconnect.
   *
   * The counterpart `joinRooms` never had. Membership here is sticky by design —
   * a reconnect builds a brand-new server-side socket and must re-ask for
   * everything — but sticky with no release means a terminal that moves from one
   * station to another keeps receiving the old station's tickets for the life of
   * the process, and re-subscribes to them on every reconnect. Whoever changes
   * the context has to say so.
   */
  leaveRooms(rooms: string[]): void;
  /**
   * True once this socket has stopped retrying because the gateway kept refusing
   * its credential. Terminal — it never goes back to false.
   */
  readonly authFailed: boolean;
}

/** A `connect_error` that is about the credential rather than the network. */
const AUTH_ERROR = /unauthor|not authenticated|invalid token|missing token|forbidden|jwt/i;

/**
 * ADR-009 — thin wrapper over the /realtime namespace.
 *
 * Socket.IO builds a **new server-side socket** on every reconnect, so all room membership is
 * lost the moment the Wi-Fi blips; the client is still `connected`, still shows a green pill,
 * and receives nothing. Hence the rejoin below.
 *
 * The rejoin used to replay only `options.rooms`, fixed at construction, but every room joined
 * *after* the first call arrives as a plain `socket.emit('join', …)` from an app's own realtime
 * helper and was never replayed — a KDS whose header opens the socket with no rooms rejoined
 * literally nothing.
 *
 * So the set of rooms is owned here and is sticky: `emit` is wrapped, and any `join` — from
 * `joinRooms()` or from a raw `socket.emit('join', …)` anywhere in any app — is remembered and
 * replayed on every `connect`. No call site has to know about it.
 *
 * X-01 — **the credential is fetched per attempt, not captured.**
 *
 * `auth` used to be the object `{ token }`, built once from a string the caller had read once.
 * Socket.IO sends that same object on every reconnect, and it reconnects for ever with backoff,
 * so the moment the access token expired the gateway refused the handshake and the client spent
 * the rest of the process lifetime re-offering a credential it had already been told was dead —
 * a wall-mounted KDS stuck on RECONNECTING with nothing in the UI to say why. Socket.IO also
 * accepts `auth` as a **function**, and calls it on every connection attempt (`Socket#onopen`,
 * bound to the manager's `open`), which is the shape a token that expires needs.
 *
 * Two halves, and both are load-bearing:
 *  - `getToken` is asked afresh per attempt, and is told when the last attempt was refused so a
 *    refreshable credential can be refreshed rather than repeated;
 *  - after `maxAuthFailures` consecutive refusals the socket **stops**, because a client that
 *    retries for ever against a verdict that will not change is indistinguishable from a quiet
 *    venue. `onAuthFailure` is how an app learns to say so.
 *
 * The gateway is not weakened by any of this: it still refuses a bad token and still hangs up.
 */
export function connectRealtime(options: RealtimeOptions): RealtimeSocket {
  // The value form stays supported for the callers that have a token which does
  // not expire under them. `undefined` still means "send no auth at all".
  const getToken: RealtimeTokenGetter | null =
    options.getToken ?? (options.token !== undefined ? () => options.token : null);

  const maxAuthFailures = Math.max(1, options.maxAuthFailures ?? DEFAULT_MAX_AUTH_FAILURES);

  /** Consecutive refusals. Reset by a connection that survives un-refused. */
  let refusals = 0;
  /** Whether the most recent handshake was the one that got refused. */
  let refusedLast = false;
  let attempt = 0;
  let authFailed = false;

  const socket = io(`${options.baseURL.replace(/\/api\/?$/, '')}/realtime`, {
    transports: ['websocket'],
    auth: getToken
      ? (cb: (data: object) => void) => {
          attempt += 1;
          const request: RealtimeTokenRequest = { attempt, refused: refusedLast };
          void (async () => {
            let token: string | null | undefined;
            try {
              token = await getToken(request);
            } catch {
              // A Keychain read that throws must not wedge the handshake open
              // for ever: answer with nothing and take the refusal, which is a
              // state this socket knows how to end.
              token = null;
            }
            cb(token ? { token } : {});
          })();
        }
      : undefined,
    reconnection: true,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 10000,
  }) as RealtimeSocket;

  const joined = new Set<string>((options.rooms ?? []).filter(Boolean));

  const rawEmit = socket.emit.bind(socket);

  // Remember the rooms of every `join`, and forget those of every `leave`,
  // whoever sends it — an app that reaches for the raw `socket.emit` must not be
  // able to leave a room the reconnect would then silently re-join.
  socket.emit = ((event: string, ...args: unknown[]) => {
    if (event === 'join' || event === 'leave') {
      const asked = (args[0] as { rooms?: unknown } | undefined)?.rooms;
      if (Array.isArray(asked)) {
        for (const room of asked) {
          if (typeof room !== 'string' || !room) continue;
          if (event === 'join') joined.add(room);
          else joined.delete(room);
        }
      }
    }
    return (rawEmit as (event: string, ...args: unknown[]) => Socket)(event, ...args);
  }) as typeof socket.emit;

  Object.defineProperty(socket, 'joinedRooms', {
    get: () => joined as ReadonlySet<string>,
    enumerable: false,
    configurable: true,
  });

  Object.defineProperty(socket, 'authFailed', {
    get: () => authFailed,
    enumerable: false,
    configurable: true,
  });

  socket.joinRooms = (rooms: string[]) => {
    const wanted = rooms.filter((room) => typeof room === 'string' && room);
    if (wanted.length) socket.emit('join', { rooms: wanted });
  };

  socket.leaveRooms = (rooms: string[]) => {
    const wanted = rooms.filter((room) => typeof room === 'string' && room);
    if (!wanted.length) return;
    // Dropped from the sticky set first, so a reconnect racing this call cannot
    // re-ask for a room the caller has just given up.
    for (const room of wanted) joined.delete(room);
    if (socket.connected) rawEmit('leave', { rooms: wanted });
  };

  let settleTimer: TimerHandle | null = null;
  let retryTimer: TimerHandle | null = null;
  let retryDelay = RECONNECT_DELAY_MS;

  const clearSettle = () => {
    if (settleTimer === null) return;
    timers.clearTimeout(settleTimer);
    settleTimer = null;
  };

  /**
   * Ask for one more handshake, later.
   *
   * Only ever called when the manager has stood down (`socket.active === false`)
   * — while it is still retrying, a second loop on top of it would double every
   * terminal's handshake rate against a backend that is trying to come up.
   *
   * Jittered because a venue's terminals are all hung up on in the same
   * millisecond by the same restart, and would otherwise come back in lockstep.
   */
  const scheduleReconnect = () => {
    if (authFailed || retryTimer !== null || socket.connected) return;
    const delay = retryDelay * (1 + Math.random() * 0.5);
    retryDelay = Math.min(retryDelay * 2, RECONNECT_DELAY_MAX_MS);
    retryTimer = timers.setTimeout(() => {
      retryTimer = null;
      if (authFailed || socket.connected) return;
      socket.connect();
    }, delay);
  };

  const cancelReconnect = () => {
    if (retryTimer === null) return;
    timers.clearTimeout(retryTimer);
    retryTimer = null;
  };

  // Every connect — the first one and every reconnect — replays the full set.
  socket.on('connect', () => {
    // The gateway accepts the namespace connection first and only then verifies,
    // so `connect` fires even for a handshake that is about to be refused: this
    // is not yet evidence of anything. `refusedLast` is what the token getter is
    // told next time, and the refusal that may be about to arrive will set it
    // again a tick from now.
    refusedLast = false;
    clearSettle();
    settleTimer = timers.setTimeout(() => {
      settleTimer = null;
      // Nothing refused this connection. The credential works; forget the past.
      refusals = 0;
      retryDelay = RECONNECT_DELAY_MS;
    }, CREDENTIAL_SETTLE_MS);
    if (joined.size) rawEmit('join', { rooms: [...joined] });
  });

  /**
   * The server said something about this credential, and it was no.
   *
   * Counted, because a verdict that will not change must eventually end the
   * socket — that is the whole reason `maxAuthFailures` exists. Every path here
   * also has to re-arm the handshake itself: the gateway answers a refusal by
   * hanging up, and Socket.IO does not come back from that.
   */
  const refuse = (message?: string) => {
    clearSettle();
    refusedLast = true;
    refusals += 1;
    if (authFailed) return;

    if (refusals < maxAuthFailures) {
      // Not proven dead yet. The next handshake is told `refused`, so the getter
      // refreshes rather than re-offering the string just rejected — the whole
      // point of asking per attempt, and unreachable until this retry existed.
      scheduleReconnect();
      return;
    }

    authFailed = true;
    cancelReconnect();
    // `socket.disconnect()`, not a flag: it destroys the socket on its manager,
    // and with no socket left the manager sets `skipReconnect` and drops the
    // backoff timer. Anything less leaves the loop running underneath.
    socket.disconnect();
    options.onAuthFailure?.({ attempts: refusals, message });
  };

  // X-01 — the gateway's own verdict. It says this before it hangs up.
  socket.on('unauthorized', (payload: { message?: string } | undefined) =>
    refuse(typeof payload?.message === 'string' ? payload.message : undefined),
  );

  /**
   * X-14 — the two failures this hook carries, and how they are told apart.
   *
   * `socket.active` is Socket.IO's own answer to "will the manager try again?".
   * A backend that is absent gives `connect_error('websocket error')` with
   * `active` still true: the manager is already on it and this client must keep
   * its hands off. A refusal raised in namespace middleware arrives as a
   * CONNECT_ERROR *packet*, which destroys the socket and leaves `active` false
   * — it reached a server, and that server answered.
   *
   * So `active === false` means "the server spoke and nobody will ask again",
   * and only then does the message decide which kind of speaking it was: a
   * verdict about the credential, which is counted, or anything else — a
   * shutting-down gateway, a refused namespace — which is not, because a server
   * with an opinion about its own health has said nothing about this token.
   */
  socket.on('connect_error', (error: Error) => {
    clearSettle();
    if (socket.active) return;
    if (AUTH_ERROR.test(error?.message ?? '')) refuse(error.message);
    else scheduleReconnect();
  });

  /**
   * X-14 — the live defect: open apps did not come back after a backend restart.
   *
   * Measured against the running gateway: a refused handshake reaches the client
   * as `disconnect('io server disconnect')`, and Socket.IO treats that reason as
   * deliberate and stands down for good — `active` false, `skipReconnect` true,
   * the token getter asked exactly once, `onAuthFailure` never called. One
   * transient refusal while the API's pool was still cold therefore killed every
   * open terminal permanently and silently, and the give-up rule that needs
   * three refusals could never see more than one.
   *
   * A restart that merely drops the transport is a different reason
   * (`transport close`) and Socket.IO handles it correctly on its own; nothing
   * here touches that path.
   */
  socket.on('disconnect', (reason: string) => {
    clearSettle();
    if (reason === 'io server disconnect') scheduleReconnect();
  });

  if (options.onJoinDenied) {
    const onDenied = options.onJoinDenied;
    socket.on('join:denied', (denied: JoinAck['denied']) => {
      if (Array.isArray(denied) && denied.length) onDenied(denied);
    });
  }

  return socket;
}

/**
 * A `getToken` backed by the same store — and the same refresh — as the HTTP client.
 *
 * Pass it the object `createHttpClient` was given, or one carrying the same
 * `tokenStore` instance; `refreshAccessTokens` is keyed on that instance, so a
 * socket reconnect and a 401 that land together share one refresh rather than
 * racing to spend a rotating refresh token twice.
 *
 * The behaviour that matters is what it does when told `refused`:
 *  - a refreshable credential is refreshed, so the reconnect presents a **new**
 *    token instead of the one the gateway just rejected;
 *  - a credential with nothing to refresh from — a §14 guest token, whose only
 *    cure is another QR scan — answers `null` rather than re-offering itself, so
 *    the socket reaches `onAuthFailure` promptly instead of retrying for ever.
 */
export function createRealtimeTokenGetter(source: TokenSource): RealtimeTokenGetter {
  return async ({ refused }) => {
    const tokens = await source.tokenStore.get();
    if (!tokens?.accessToken) return null;
    if (!refused) return tokens.accessToken;
    if (!tokens.refreshToken) return null;

    const refreshed = await refreshAccessTokens(source);
    return refreshed?.accessToken ?? null;
  };
}

export function onVenueEvent<T>(
  socket: Socket,
  event: VenueEventName,
  handler: (payload: T) => void,
): () => void {
  socket.on(event, handler);
  return () => socket.off(event, handler);
}
